import { buildWorkflowBatches, type WorkflowHistoryItem, type WorkflowWorkspaceResponse } from '@shared/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { captureGenerationSession, trackWorkflowGenerations } from '@/lib/generation-notifications'

import { api, type CreateWorkflowRequest, type SubmitWorkflowRunRequest, type WorkflowRunSubmission } from './api'

const isActiveRun = (status: string) => status === 'pending' || status === 'processing'
const WORKFLOW_GRAPH_SETTLING_WINDOW_MS = 90_000

/**
 * Workflow runs and assets are read through DynamoDB GSIs, which can lag the
 * workspace row immediately after a transaction. Treat a fresh, incomplete
 * graph as still loading rather than presenting an empty editor as if it were
 * a valid workflow with no generations.
 */
export function isWorkflowGraphHydrating(workspace: WorkflowWorkspaceResponse, now = Date.now()) {
  const expectedAssetCount = workspace.runs.reduce((count, run) => count + run.assetIds.length, 0)
  const graphIsComplete = workspace.runs.length > 0 && workspace.assets.length >= expectedAssetCount
  if (graphIsComplete) return false

  const lastUpdatedAt = Date.parse(workspace.workflow.updatedAt)
  return Number.isFinite(lastUpdatedAt) && now - lastUpdatedAt < WORKFLOW_GRAPH_SETTLING_WINDOW_MS
}

type WorkspaceRecord = { createdAt: string; updatedAt: string }

/** ISO timestamps sort lexically; the fresher read wins ties. */
const newerRecord = <T extends { updatedAt: string }>(cached: T, fetched: T) =>
  cached.updatedAt > fetched.updatedAt ? cached : fetched

/**
 * Combines a fetched workspace with the cached one. The GSI-backed graph can
 * briefly omit a run that was just submitted, or return an older status for
 * one already seen. Dropping that run would hide the new batch and stop
 * polling, so records missing from the fetch are kept while they are still
 * within the settling window, and every record keeps its latest known state.
 */
export function mergeWorkflowWorkspace(
  cached: WorkflowWorkspaceResponse | undefined,
  fetched: WorkflowWorkspaceResponse,
  now = Date.now()
): WorkflowWorkspaceResponse {
  if (cached?.workflow.workflowId !== fetched.workflow.workflowId) return fetched

  const isSettling = (record: WorkspaceRecord) => {
    const createdAt = Date.parse(record.createdAt)
    return Number.isFinite(createdAt) && now - createdAt < WORKFLOW_GRAPH_SETTLING_WINDOW_MS
  }
  const mergeById = <T extends WorkspaceRecord>(
    cachedRecords: T[],
    fetchedRecords: T[],
    idOf: (record: T) => string
  ) => {
    const merged = new Map(fetchedRecords.map((record) => [idOf(record), record]))
    cachedRecords.forEach((record) => {
      const fetchedRecord = merged.get(idOf(record))
      if (fetchedRecord) merged.set(idOf(record), newerRecord(record, fetchedRecord))
      else if (isSettling(record)) merged.set(idOf(record), record)
    })
    return [...merged.values()]
  }
  const runs = mergeById(cached.runs, fetched.runs, (run) => run.runId)
  const assets = mergeById(cached.assets, fetched.assets, (asset) => asset.assetId)

  return {
    workflow: newerRecord(cached.workflow, fetched.workflow),
    runs,
    assets,
    batches: buildWorkflowBatches(runs, assets)
  }
}

/**
 * Adds a submitted batch to the cached workspace. A resent request returns the
 * batch its first attempt created, which polling may already have loaded, so
 * records the cache already has keep their cached, possibly newer, state.
 */
export function appendSubmittedBatch(
  current: WorkflowWorkspaceResponse,
  submission: WorkflowRunSubmission
): WorkflowWorkspaceResponse {
  const knownRunIds = new Set(current.runs.map((run) => run.runId))
  const knownAssetIds = new Set(current.assets.map((asset) => asset.assetId))
  const runs = [...current.runs, ...submission.runs.filter((run) => !knownRunIds.has(run.runId))]
  const assets = [...current.assets, ...submission.assets.filter((asset) => !knownAssetIds.has(asset.assetId))]

  return {
    ...current,
    workflow: { ...current.workflow, status: 'active' },
    runs,
    assets,
    batches: buildWorkflowBatches(runs, assets)
  }
}

export function shouldRefreshWorkflowWorkspace(workspace: WorkflowWorkspaceResponse, now = Date.now()) {
  return isWorkflowGraphHydrating(workspace, now) || workspace.runs.some((run) => isActiveRun(run.status))
}

export const workflowKeys = {
  all: ['workflows'] as const,
  workspace: (workflowId: string) => [...workflowKeys.all, 'workspace', workflowId] as const,
  history: (creator = '') => [...workflowKeys.all, 'history', ...(creator ? [creator] : [])] as const
}

export function useWorkflowHistory(creator = '') {
  return useQuery<WorkflowHistoryItem[]>({
    queryKey: workflowKeys.history(creator),
    queryFn: async () => (await api.getWorkflows(creator)).workflows,
    staleTime: 30000,
    refetchInterval: (query) => (query.state.data?.some((workflow) => workflow.status === 'active') ? 2500 : false)
  })
}

export function useWorkflow(workflowId: string | null) {
  const queryClient = useQueryClient()
  return useQuery<WorkflowWorkspaceResponse>({
    queryKey: workflowKeys.workspace(workflowId ?? ''),
    queryFn: async () => {
      if (!workflowId) throw new Error('Workflow ID is required')
      const fetched = await api.getWorkflow(workflowId)
      return mergeWorkflowWorkspace(
        queryClient.getQueryData<WorkflowWorkspaceResponse>(workflowKeys.workspace(workflowId)),
        fetched
      )
    },
    enabled: !!workflowId,
    refetchInterval: (query) => (query.state.data && shouldRefreshWorkflowWorkspace(query.state.data) ? 2500 : false)
  })
}

export function useCreateWorkflow() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: CreateWorkflowRequest) => api.createWorkflow(request),
    onMutate: captureGenerationSession,
    onSuccess: (response, _, session) => {
      trackWorkflowGenerations(response.workflow.workflowId, response.runs, session)
      queryClient.setQueryData<WorkflowWorkspaceResponse>(workflowKeys.workspace(response.workflow.workflowId), {
        workflow: response.workflow,
        runs: response.runs,
        assets: response.assets,
        batches: buildWorkflowBatches(response.runs, response.assets)
      })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.workspace(response.workflow.workflowId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history() })
    }
  })
}

export function useSubmitWorkflowRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: SubmitWorkflowRunRequest) => api.submitWorkflowRun(request),
    onMutate: captureGenerationSession,
    onSuccess: (response, request, session) => {
      trackWorkflowGenerations(request.workflowId, response.runs, session)
      queryClient.setQueryData<WorkflowWorkspaceResponse>(workflowKeys.workspace(request.workflowId), (current) =>
        current ? appendSubmittedBatch(current, response) : current
      )
      void queryClient.invalidateQueries({ queryKey: workflowKeys.workspace(request.workflowId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history() })
    }
  })
}

export function useDeleteWorkflow() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (workflowId: string) => api.deleteWorkflow(workflowId),
    onSuccess: (_, workflowId) => {
      queryClient.removeQueries({ queryKey: workflowKeys.workspace(workflowId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history() })
    }
  })
}
